import { useState, useCallback, useEffect } from 'react';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { PurchaseOrderData, PurchaseOrderStep, PurchaseOrderValidation, ValidationError, Signature, FileUploadProgress } from '@/components/campaign/types/purchaseOrderV2';
import { generatePONumber } from '@/components/campaign/utils/purchaseOrderUtils';
import { devError } from '@/utils/secureLogger';
import { secureStorage } from '@/utils/secureStorage';

const STEPS: PurchaseOrderStep[] = [
  { id: 'review', title: 'Review Details', description: 'Review campaign and pricing information', isComplete: false, isActive: true },
  { id: 'signature', title: 'Digital Signature', description: 'Provide your digital signature', isComplete: false, isActive: false },
  { id: 'documents', title: 'Upload Documents', description: 'Upload required company documents', isComplete: false, isActive: false },
  { id: 'submit', title: 'Submit Order', description: 'Submit your purchase order', isComplete: false, isActive: false }
];

export const usePurchaseOrderV2 = (
  campaignId: string,
  initialCompanyDetails: any,
  devices: number,
  duration: number,
  amount: number,
  onComplete: () => void
) => {
  const { toast } = useToast();
  
  // Core state
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [steps, setSteps] = useState<PurchaseOrderStep[]>(STEPS);
  
  // Purchase order data - use lazy initializer to get budget from storage
  const [purchaseOrderData, setPurchaseOrderData] = useState<PurchaseOrderData>(() => {
    const storedBudget = secureStorage.get<any>('budgetCalculations');
    const correctAmount = storedBudget?.totalBudget || storedBudget?.totalAmount || amount;
    
    return {
      poNumber: generatePONumber(),
      campaignId,
      companyDetails: initialCompanyDetails,
      devices,
      duration,
      amount: correctAmount,
      selectedLocations: [],
      budgetCalculations: storedBudget || {
        baseTotal: 0,
        discounts: [],
        subtotal: 0,
        vatAmount: 0,
        totalAmount: correctAmount,
        costPerDevicePerDay: 25
      },
      signature: null,
      stamp: null,
      companyRegistration: null,
      vatRegistration: null,
      termsAccepted: false,
      status: 'draft',
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString()
    };
  });

  // UI state
  const [validation, setValidation] = useState<PurchaseOrderValidation>({
    isValid: false,
    errors: [],
    warnings: []
  });
  
  const [fileUploadProgress, setFileUploadProgress] = useState<Record<string, FileUploadProgress>>({});

  // Load initial data from secureStorage
  useEffect(() => {
    const loadStoredData = () => {
      try {
        // Load selected locations
        const locations = secureStorage.get<any[]>('selectedLocations');
        if (locations) {
          setPurchaseOrderData(prev => ({
            ...prev,
            selectedLocations: locations,
            updatedAt: new Date().toISOString()
          }));
        }

        // Load budget calculations and update amount
        const budget = secureStorage.get<any>('budgetCalculations');
        if (budget) {
          const totalAmount = budget.totalBudget || budget.totalAmount;
          if (totalAmount && totalAmount > 0) {
            setPurchaseOrderData(prev => ({
              ...prev,
              budgetCalculations: budget,
              amount: totalAmount,
              updatedAt: new Date().toISOString()
            }));
          }
        }
      } catch (error) {
        devError('Error loading stored data:', error);
      }
    };

    loadStoredData();
  }, [amount]);

  // Update purchase order data
  const updatePurchaseOrderData = useCallback((updates: Partial<PurchaseOrderData>) => {
    setPurchaseOrderData(prev => ({
      ...prev,
      ...updates,
      updatedAt: new Date().toISOString()
    }));
  }, []);

  // Validate current step
  const validateCurrentStep = useCallback((): PurchaseOrderValidation => {
    const errors: ValidationError[] = [];
    const warnings: string[] = [];

    switch (steps[currentStep]?.id) {
      case 'review':
        if (!purchaseOrderData.companyDetails?.email) {
          errors.push({ field: 'email', message: 'Email address is required' });
        }
        if (!purchaseOrderData.amount || purchaseOrderData.amount <= 0) {
          errors.push({ field: 'amount', message: 'Campaign amount is required and must be greater than 0' });
        }
        if (!purchaseOrderData.selectedLocations.length) {
          warnings.push('No locations selected for this campaign');
        }
        break;

      case 'signature':
        if (!purchaseOrderData.signature) {
          errors.push({ field: 'signature', message: 'Digital signature is required' });
        }
        if (!purchaseOrderData.stamp) {
          errors.push({ field: 'stamp', message: 'Company stamp is required' });
        }
        break;

      case 'documents':
        if (!purchaseOrderData.companyRegistration) {
          errors.push({ field: 'companyRegistration', message: 'Company registration document is required' });
        }
        break;
    }

    const validation = {
      isValid: errors.length === 0,
      errors,
      warnings
    };

    setValidation(validation);
    return validation;
  }, [currentStep, purchaseOrderData, steps]);

  // Navigate to specific step
  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < steps.length) {
      setCurrentStep(stepIndex);
      setSteps(prev => prev.map((step, index) => ({
        ...step,
        isActive: index === stepIndex
      })));
    }
  }, [steps.length]);

  // Go to next step
  const nextStep = useCallback(() => {
    const validation = validateCurrentStep();
    
    if (!validation.isValid) {
      toast({
        title: "Validation Error",
        description: validation.errors[0]?.message || "Please complete all required fields",
        variant: "destructive",
      });
      return false;
    }

    // Mark current step as complete
    setSteps(prev => prev.map((step, index) => ({
      ...step,
      isComplete: index === currentStep ? true : step.isComplete
    })));

    if (currentStep < steps.length - 1) {
      goToStep(currentStep + 1);
      return true;
    }
    
    return false;
  }, [currentStep, validateCurrentStep, goToStep, steps.length, toast]);

  // Go to previous step
  const previousStep = useCallback(() => {
    if (currentStep > 0) {
      goToStep(currentStep - 1);
    }
  }, [currentStep, goToStep]);

  // Handle signature save
  const handleSignatureSave = useCallback((signatureData: string, type: 'draw' | 'upload') => {
    const signature: Signature = {
      data: signatureData,
      type,
      timestamp: new Date().toISOString()
    };
    updatePurchaseOrderData({ signature });
  }, [updatePurchaseOrderData]);

  // Handle stamp upload
  const handleStampUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === 'string') {
          updatePurchaseOrderData({ stamp: reader.result });
        }
      };
      reader.readAsDataURL(file);
    }
  }, [updatePurchaseOrderData]);

  // Handle stamp removal
  const handleStampRemove = useCallback(() => {
    updatePurchaseOrderData({ stamp: null });
  }, [updatePurchaseOrderData]);

  // Handle file upload with progress
  const handleFileUpload = useCallback(async (
    file: File,
    type: 'companyRegistration' | 'vatRegistration'
  ) => {
    const uploadId = `${type}-${Date.now()}`;
    
    // Initialize progress
    setFileUploadProgress(prev => ({
      ...prev,
      [uploadId]: {
        file,
        progress: 0,
        status: 'uploading'
      }
    }));

    try {
      // Validate file
      if (file.type !== 'application/pdf') {
        throw new Error('Only PDF files are allowed');
      }
      
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size must be less than 10MB');
      }

      // Simulate upload progress (in real implementation, this would track actual upload)
      const progressInterval = setInterval(() => {
        setFileUploadProgress(prev => {
          const current = prev[uploadId];
          if (!current || current.progress >= 90) {
            clearInterval(progressInterval);
            return prev;
          }
          
          return {
            ...prev,
            [uploadId]: {
              ...current,
              progress: Math.min(current.progress + 10, 90)
            }
          };
        });
      }, 100);

      // Update purchase order data
      updatePurchaseOrderData({ [type]: file });

      // Complete progress
      setFileUploadProgress(prev => ({
        ...prev,
        [uploadId]: {
          file,
          progress: 100,
          status: 'completed'
        }
      }));

      toast({
        title: "File uploaded successfully",
        description: `${type === 'companyRegistration' ? 'Company registration' : 'VAT registration'} document uploaded`,
      });

    } catch (error) {
      setFileUploadProgress(prev => ({
        ...prev,
        [uploadId]: {
          file,
          progress: 0,
          status: 'error',
          error: error instanceof Error ? error.message : 'Upload failed'
        }
      }));

      toast({
        title: "Upload Error",
        description: error instanceof Error ? error.message : 'Failed to upload file',
        variant: "destructive",
      });
    }
  }, [updatePurchaseOrderData, toast]);

  // Submit purchase order
  const submitPurchaseOrder = useCallback(async () => {
    const validation = validateCurrentStep();
    
    if (!validation.isValid) {
      toast({
        title: "Validation Error",
        description: "Please complete all required fields before submitting",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Mark this as a purchase order submission to prevent duplicate emails
      localStorage.setItem('isPurchaseOrder', 'true');

      // Upload documents to storage
      let companyRegUrl = '';
      let vatRegUrl = '';

      if (purchaseOrderData.companyRegistration) {
        const companyRegFile = purchaseOrderData.companyRegistration;
        const companyRegFileName = `${Date.now()}-company-registration.pdf`;
        
        const { error: companyRegError, data: companyRegData } = await supabase.storage
          .from('company_documents')
          .upload(companyRegFileName, companyRegFile);

        if (companyRegError) throw companyRegError;

        const { data: { publicUrl } } = supabase.storage
          .from('company_documents')
          .getPublicUrl(companyRegFileName);
        
        companyRegUrl = publicUrl;
      }

      if (purchaseOrderData.vatRegistration) {
        const vatRegFile = purchaseOrderData.vatRegistration;
        const vatRegFileName = `${Date.now()}-vat-registration.pdf`;
        
        const { error: vatRegError } = await supabase.storage
          .from('company_documents')
          .upload(vatRegFileName, vatRegFile);

        if (!vatRegError) {
          const { data: { publicUrl } } = supabase.storage
            .from('company_documents')
            .getPublicUrl(vatRegFileName);
          
          vatRegUrl = publicUrl;
        }
      }

      // Create purchase order in database
      const creativeUrls = secureStorage.get<string[]>('creativeUrls') || [];
      
      const { data: createData, error: createError } = await supabase.functions.invoke('create-purchase-order-v2', {
        body: {
          ...purchaseOrderData,
          termsAccepted: true, // Auto-accept terms to align with current UI
          companyRegistrationUrl: companyRegUrl,
          vatRegistrationUrl: vatRegUrl,
          creativeUrls: creativeUrls
        }
      });

      if (createError) {
        devError('Create purchase order error:', createError);
        throw createError;
      }
      
      // Check for error in response body
      if (createData && !createData.success) {
        const errorMsg = createData.error?.message || 'Failed to create purchase order';
        devError('Create purchase order failed:', errorMsg);
        throw new Error(errorMsg);
      }

      // Try to send purchase order email, but don't block if it fails
      try {
        const { error: emailError } = await supabase.functions.invoke('send-purchase-order-v2', {
          body: {
            ...purchaseOrderData,
            companyRegistrationUrl: companyRegUrl,
            vatRegistrationUrl: vatRegUrl
          }
        });

        if (emailError) {
          devError('Email sending failed, but purchase order was created:', emailError);
        }
      } catch (emailError) {
        devError('Failed to send purchase order email:', emailError);
        // Don't throw - email is optional, PO was already created successfully
      }

      toast({
        title: "Success!",
        description: "Purchase order has been submitted successfully",
      });

      // Clean up the PO flag after a delay to allow payment completion to check it
      setTimeout(() => {
        localStorage.removeItem('isPurchaseOrder');
      }, 5000);

      onComplete();

    } catch (error) {
      devError('Error submitting purchase order:', error);
      toast({
        title: "Submission Error",
        description: error instanceof Error ? error.message : "Failed to submit purchase order",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  }, [purchaseOrderData, validateCurrentStep, toast, onComplete]);

  // Auto-save to secureStorage with 1 hour expiration
  useEffect(() => {
    const autoSave = () => {
      try {
        secureStorage.set('purchaseOrderDraft', purchaseOrderData, 3600);
      } catch (error) {
        devError('Error auto-saving purchase order:', error);
      }
    };

    const timeoutId = setTimeout(autoSave, 5000); // Optimized to 5 seconds
    return () => clearTimeout(timeoutId);
  }, [purchaseOrderData]);

  return {
    // State
    purchaseOrderData,
    currentStep,
    steps,
    validation,
    isSubmitting,
    fileUploadProgress,

    // Actions
    updatePurchaseOrderData,
    goToStep,
    nextStep,
    previousStep,
    handleSignatureSave,
    handleStampUpload,
    handleStampRemove,
    handleFileUpload,
    submitPurchaseOrder,
    validateCurrentStep,

    // Computed
    canGoNext: validation.isValid,
    canGoPrevious: currentStep > 0,
    isLastStep: currentStep === steps.length - 1,
    progress: ((currentStep + 1) / steps.length) * 100
  };
};